import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ItemTypes } from '@app/lists/shared/list-detail-management/model/list-detail-management-view.model';
import { InventoryQuantityUpdate } from '@inventory/models/inventory-quantity.model';
import {
  InventoryItem,
  InventoryProductViewModel,
  InventoryViewModel,
} from '@inventory/models/inventory-view.model';
import { PlatformEnum } from '@panamax/app-state';
import { InventoryWorksheetEditSortOptions } from '@usf/ngrx-inventory';
import { SortableSectionHeader } from '../../../../../shared/components/sortable-section/models/sortable-section.model';
import { InventoryWorksheetMode } from '../../../../constants/inventory-constants';
import {
  InventoryConversionFactorUpdate,
  InventoryGroupDropdownEvent,
  InventoryItemSelectionUpdate,
  InventoryLineNumberEvent,
  InventoryPriceUpdate,
  InventoryToolTipMessaging,
  InventoryUOMDropdownEvent,
} from '../../../../models/edit-inventory.model';
import { InventorySortableSectionHeaderUpdate } from '../../../../models/inventory-sortable-section.model';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-inventory-group-sort-product-data',
  templateUrl: './inventory-group-sort-product-data.component.html',
  styleUrls: ['./inventory-group-sort-product-data.component.scss'],
})
export class InventoryGroupSortProductDataComponent {
  readonly PlatformEnum = PlatformEnum;
  readonly ItemTypes = ItemTypes;
  readonly InventoryWorksheetMode = InventoryWorksheetMode;

  @Input() platform: PlatformEnum;
  @Input() inventory: InventoryViewModel;
  @Input() isOnline: boolean;

  @Output()
  updateSortEvent: EventEmitter<InventorySortableSectionHeaderUpdate> = new EventEmitter();
  @Output()
  selectionEvent: EventEmitter<InventoryItemSelectionUpdate> = new EventEmitter();
  @Output()
  updateGroupEvent: EventEmitter<InventoryGroupDropdownEvent> = new EventEmitter();
  @Output()
  updateUOMEvent: EventEmitter<InventoryUOMDropdownEvent> = new EventEmitter();
  @Output()
  updateQuantityEvent: EventEmitter<InventoryQuantityUpdate> = new EventEmitter();
  @Output()
  updatePriceOverrideEvent: EventEmitter<InventoryPriceUpdate> = new EventEmitter();
  @Output()
  updateConversionFactorEvent: EventEmitter<InventoryConversionFactorUpdate> = new EventEmitter();
  @Output()
  productDescriptionClick: EventEmitter<InventoryProductViewModel> = new EventEmitter();
  @Output()
  addUSFProductsClick: EventEmitter<Event> = new EventEmitter();
  @Output()
  toolTipHoverEvent: EventEmitter<InventoryToolTipMessaging> = new EventEmitter();
  @Output()
  toolTipLeaveEvent: EventEmitter<void> = new EventEmitter();
  @Output()
  itemForInformationEvent: EventEmitter<InventoryProductViewModel> = new EventEmitter();
  @Output()
  resetPreviousHotkeyIdEvent: EventEmitter<void> = new EventEmitter();
  @Output()
  lineNumberEvent: EventEmitter<InventoryLineNumberEvent> = new EventEmitter();
  constructor(readonly datePipe: DatePipe) {}

  trackItem(index: any, item: InventoryItem) {
    let identifier;
    if ('productNumber' in item) {
      identifier = item.productNumber + item.groupName;
    } else {
      identifier = item.groupName + index;
    }
    return identifier;
  }

  async handleSortEvent(
    groupName: string,
    sortableSectionHeader: SortableSectionHeader,
  ) {
    if (
      this.platform === PlatformEnum.desktop &&
      sortableSectionHeader.headerName !==
        InventoryWorksheetEditSortOptions.group
    ) {
      this.updateSortEvent.emit({
        groupName,
        ...sortableSectionHeader,
      });
    }
  }

  async handleSelectionEvent(
    itemSelectionUpdate: InventoryItemSelectionUpdate,
  ) {
    this.selectionEvent.emit(itemSelectionUpdate);
  }

  async handleGroupEvent(groupDropdownEvent: InventoryGroupDropdownEvent) {
    this.updateGroupEvent.emit(groupDropdownEvent);
  }

  async handleUOMEvent(uomDropdownEvent: InventoryUOMDropdownEvent) {
    this.updateUOMEvent.emit(uomDropdownEvent);
  }

  async handleQuantityEvent(quantityUpdate: InventoryQuantityUpdate) {
    this.updateQuantityEvent.emit(quantityUpdate);
  }

  async handleConversionFactorEvent(
    conversionFactorUpdate: InventoryConversionFactorUpdate,
  ) {
    this.updateConversionFactorEvent.emit(conversionFactorUpdate);
  }

  async handlePriceOverridEvent(priceOverrideUpdate: InventoryPriceUpdate) {
    this.updatePriceOverrideEvent.emit({
      ...priceOverrideUpdate,
      inventoryNameAndDate:
        this.inventory.inventoryName +
        '-' +
        this.datePipe.transform(this.inventory.inventoryDate, 'M/d/yyyy'),
    });
  }

  async handleToolTipHover(event$: InventoryToolTipMessaging) {
    this.toolTipHoverEvent.emit(event$);
  }
  async handleTooltipLeave() {
    this.toolTipLeaveEvent.emit();
  }

  handleProductDescriptionClick(item: InventoryProductViewModel) {
    this.productDescriptionClick.emit(item);
  }

  async openAddProductsPopover(event: Event) {
    this.addUSFProductsClick.emit(event);
  }

  async handleInfoIconClick(item: InventoryProductViewModel) {
    this.itemForInformationEvent.emit(item);
  }

  resetPreviousHotkeyId() {
    this.resetPreviousHotkeyIdEvent.emit();
  }

  handleLineNumberChange(inventoryLineNumberEvent: InventoryLineNumberEvent) {
    this.lineNumberEvent.emit(inventoryLineNumberEvent);
  }
}
